<template>
  <q-page padding>
    <div class="text-h5 q-mb-md">Email Assistant</div>

    <!-- Bullets input with mic + speech-language selector floating inside bottom-right -->
    <div class="relative-position">
      <q-input
        v-model="bullets"
        type="textarea"
        autogrow
        outlined
        :dir="bulletsDir"
        :class="[bulletsRtlClass, 'multiline-placeholder']"
        :input-style="bulletsInputStyle"
        :placeholder="currentPlaceholder"
      >
      </q-input>

      <!-- Speech language + mic (bottom-right) -->
      <div class="absolute-bottom-right q-ma-sm row items-center q-gutter-sm">
        <q-select
          v-model="speechLanguage"
          :options="speechLanguageOptions"
          dense
          outlined
          emit-value
          map-options
          options-dense
          style="width: 190px"
          :disable="isListening"
          label="Speech language"
        />
        <q-btn
          :color="isListening ? 'negative' : 'primary'"
          :icon="isListening ? 'mic_off' : 'mic'"
          round
          dense
          @click="toggleVoice"
        >
          <q-tooltip>{{ isListening ? 'Stop' : 'Speak' }}</q-tooltip>
        </q-btn>
      </div>
    </div>

    <div class="text-caption q-mt-xs" v-if="isListening">
      Listening… (speech: {{ speechLanguageLabel }}) — say "stop listening" to
      stop.
    </div>

    <!-- Target Language selector (EMAIL OUTPUT) -->
    <div class="row q-col-gutter-md q-mb-md q-mt-md">
      <div class="col-12 col-sm-4">
        <q-select
          v-model="language"
          :options="languageOptions"
          label="Email Language"
          outlined
          dense
          emit-value
          map-options
        />
      </div>
    </div>

    <!-- Generate -->
    <div class="q-mt-md">
      <q-btn
        color="primary"
        :label="loadingGenerate ? 'Generating…' : 'Generate Email'"
        :loading="loadingGenerate"
        @click="onGenerate"
      />
    </div>

    <!-- Generated email editor -->
    <div v-if="draft" class="q-mt-xl q-gutter-md">
      <div class="text-subtitle1">Subject</div>
      <q-input v-model="draft.subject" :readonly="!editMode" outlined />

      <div class="text-subtitle1">Body</div>
      <q-input
        v-model="draft.body"
        :readonly="!editMode"
        type="textarea"
        autogrow
        outlined
      />

      <div class="row q-col-gutter-md q-mt-sm">
        <div class="col-12 col-sm-6">
          <q-select
            v-model="toEmails"
            use-input
            use-chips
            multiple
            input-debounce="0"
            label="Recipient email(s)"
            hint="Press Enter to add multiple emails"
            outlined
            new-value-mode="add-unique"
            :rules="[
              val => val && val.length > 0 || 'At least one recipient required',
              val => validateEmails(val)
            ]"
          >
            <template v-slot:prepend>
              <q-icon name="email" />
            </template>
            <template v-slot:hint>
              <span class="text-caption text-grey-7">
                {{ toEmails.length }} recipient(s) • Press Enter to add
              </span>
            </template>
          </q-select>
        </div>
        <div class="col-12 col-sm-6">
          <q-select
            v-model="provider"
            :options="providerOptions"
            label="Send with"
            outlined
            emit-value
            map-options
          />
        </div>
      </div>

      <!-- Actions: right-aligned same row -->
      <div class="q-mt-md row justify-end q-gutter-sm">
        <q-btn
          color="secondary"
          :label="editMode ? 'Lock Email' : 'Edit Email'"
          outline
          :disable="!draft"
          @click="editMode = !editMode"
        />
        <q-btn
          color="primary"
          :label="loadingSend ? 'Sending…' : 'Send Email'"
          :loading="loadingSend"
          :disable="!draft"
          @click="onSend"
        />
      </div>

      <!-- Status badges -->
      <div class="q-mt-sm">
        <q-badge v-if="draft.status" :color="statusColor(draft.status)" outline>
          {{ draft.status }}
        </q-badge>
        <q-badge v-if="draft.provider" color="grey-8" outline class="q-ml-sm">
          {{ draft.provider }}
        </q-badge>
        <q-badge v-if="draft.sent_at" color="grey-8" outline class="q-ml-sm">
          Sent: {{ new Date(draft.sent_at).toLocaleString() }}
        </q-badge>
      </div>
    </div>

    <!-- Email History Section -->
    <div v-if="history.length > 0 && showHistory" class="q-mt-xl">
      <div class="text-subtitle1 q-mb-md">
        Recent Emails ({{ history.length }})
      </div>
      <div class="row q-col-gutter-md">
        <!-- Email Body Boxes (full width) -->
        <div class="col-12">
          <div class="row q-col-gutter-md">
            <div
              v-for="(email, index) in history"
              :key="'body-' + index"
              class="col-12"
            >
              <q-card class="rounded-borders">
                <q-card-section>
                  <div class="text-subtitle2 text-weight-bold q-mb-sm">
                    {{ email.subject }}
                  </div>
                  <div class="text-caption text-grey-7 q-mb-md">
                    To: {{ email.to_email }} •
                    {{ new Date(email.sent_at).toLocaleString() }}
                  </div>
                  <div
                    :class="[
                      'email-body-text',
                      expandedEmails[index] ? 'expanded' : 'collapsed',
                    ]"
                  >
                    {{ email.body }}
                  </div>
                </q-card-section>
                <q-card-section class="row q-gutter-sm">
                  <q-btn
                    flat
                    dense
                    color="primary"
                    size="sm"
                    :icon="
                      expandedEmails[index] ? 'expand_less' : 'expand_more'
                    "
                    :label="expandedEmails[index] ? 'Collapse' : 'Expand'"
                    @click="toggleExpand(index)"
                  />
                  <q-btn
                    flat
                    dense
                    color="primary"
                    icon="content_copy"
                    size="sm"
                    @click="copyEmail(email)"
                  />
                  <q-btn
                    flat
                    dense
                    color="primary"
                    icon="forward"
                    size="sm"
                    @click="resendEmail(email)"
                  />
                  <q-badge
                    v-if="email.status"
                    :color="statusColor(email.status)"
                    outline
                    class="q-ml-auto"
                  >
                    {{ email.status }}
                  </q-badge>
                </q-card-section>
              </q-card>
            </div>
          </div>
        </div>
      </div>
    </div>
  </q-page>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useQuasar } from 'quasar';
import { api } from '../api';

const $q = useQuasar();

/* -------- user state -------- */
const loggedInUser = ref(null);
const userDisplayName = computed(() =>
  loggedInUser.value?.employee?.full_name ||
  loggedInUser.value?.full_name ||
  loggedInUser.value?.name ||
  loggedInUser.value?.username ||
  loggedInUser.value?.email ||
  null,
);

function decodeJwtUser(token) {
  try {
    const [, payload] = token.split('.');
    const json = JSON.parse(atob(payload));
    return json || null;
  } catch (e) {
    return null;
  }
}

function loadUserFromStorage() {
  const keys = ['user', 'me'];
  for (const key of keys) {
    const raw = localStorage.getItem(key);
    if (!raw) continue;

    // JSON string
    if (raw.trim().startsWith('{')) {
      try {
        return JSON.parse(raw);
      } catch (e) {
        console.warn('Failed to parse user from', key, e);
      }
    }

    // JWT token
    if (raw.includes('.')) {
      const decoded = decodeJwtUser(raw);
      if (decoded) return decoded;
    }
  }
  return null;
}

/* -------- form state -------- */
const bullets = ref('');
const toEmails = ref([]); // Changed from toEmail to toEmails array
const provider = ref('');
const providerOptions = [
  { label: 'Gmail', value: 'gmail' },
  { label: 'Outlook (Office365)', value: 'outlook' },
  { label: 'Yahoo', value: 'yahoo' },
];

/* -------- Email validation -------- */
function validateEmails(emails) {
  if (!emails || emails.length === 0) {
    return 'At least one recipient required';
  }
  
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const invalidEmails = emails.filter(email => !emailRegex.test(email));
  
  if (invalidEmails.length > 0) {
    return `Invalid email(s): ${invalidEmails.join(', ')}`;
  }
  
  return true;
}

/* -------- generated draft -------- */
const draft = ref(null);
const editMode = ref(true);

/* -------- loading states -------- */
const loadingGenerate = ref(false);
const loadingSend = ref(false);

/* -------- tone/language -------- */
const tone = ref('Professional');

/* Target language (EMAIL OUTPUT) */
const language = ref('Select Target Language');
const languageOptions = [
  { label: 'English', value: 'English' },
  { label: 'German', value: 'German' },
  { label: 'Turkish', value: 'Turkish' },
  { label: 'Arabic', value: 'Arabic' },
  { label: 'French', value: 'French' },
  { label: 'Spanish', value: 'Spanish' },
  { label: 'Swahili', value: 'Swahili' },
];

// Placeholder text per language
const languagePlaceholders = {
  English: `- Write your first point clearly
- Add a second one here
- You can speak or type`,
  German: `- Schreiben Sie Ihren ersten Punkt klar
- Fügen Sie hier einen zweiten hinzu
- Sie können sprechen oder tippen`,
  Turkish: `- İlk noktanızı açıkça yazın
- Buraya ikinci bir nokta ekleyin
- Konuşabilir veya yazabilirsiniz`,
  Arabic: `- اكتب النقطة الأولى بوضوح
- أضف نقطة ثانية هنا
- يمكنك التحدث أو الكتابة`,
  French: `- Écrivez clairement votre premier point
- Ajoutez-en un deuxième ici
- Vous pouvez parler ou taper`,
  Spanish: `- Escribe claramente tu primer punto
- Añade un segundo aquí
- Puedes hablar o escribir`,
  Swahili: `- Andika hoja yako ya kwanza kwa uwazi
- Ongeza hoja ya pili hapa
- Unaweza kuzungumza au kuandika`,
};

/* ✅ Speech input language dropdown (INPUT for mic only) */
const speechLanguage = ref(null);
const speechLanguageOptions = [
  { label: 'English', value: 'en-US' },
  { label: 'German', value: 'de-DE' },
  { label: 'Turkish', value: 'tr-TR' },
  { label: 'Arabic', value: 'ar-SA' },
  { label: 'French', value: 'fr-FR' },
  { label: 'Spanish', value: 'es-ES' },
  { label: 'Swahili', value: 'sw-KE' },
];

const speechLanguageLabel = computed(() => {
  const found = speechLanguageOptions.find(
    (o) => o.value === speechLanguage.value,
  );
  return found?.label || speechLanguage.value;
});

/* ✅ Map speech locale -> placeholder key */
const speechLocaleToPlaceholder = {
  'en-US': 'English',
  'de-DE': 'German',
  'tr-TR': 'Turkish',
  'ar-SA': 'Arabic',
  'fr-FR': 'French',
  'es-ES': 'Spanish',
  'sw-KE': 'Swahili',
};

/* ✅ Placeholder follows spoken language */
const currentPlaceholder = computed(() => {
  const key = speechLocaleToPlaceholder[speechLanguage.value] || 'English';
  return languagePlaceholders[key] || languagePlaceholders.English;
});

function notify(type, message) {
  $q.notify({ type, message, position: 'top-right' });
}

function statusColor(s) {
  if (s === 'SENT') return 'positive';
  if (s === 'FAILED') return 'negative';
  return 'info';
}

/* ✅ RTL detection based on spoken language */
const isRtl = computed(() => speechLanguage.value === 'ar-SA');
const bulletsDir = computed(() => (isRtl.value ? 'rtl' : 'ltr'));

/* Keep your existing input style, but add textAlign based on RTL */
const bulletsInputStyle = computed(() => ({
  minHeight: '200px',
  lineHeight: '1.6em',
  fontFamily: 'Menlo, monospace',
  whiteSpace: 'pre-line',
  textAlign: isRtl.value ? 'right' : 'left',
}));

/* Optional class hook (if you want more RTL styling later) */
const bulletsRtlClass = computed(() => (isRtl.value ? 'rtl-bullets' : ''));

/* Email history */
const history = ref([]);

/* History visibility control */
const showHistory = ref(true);

/* Email body expand state */
const expandedEmails = ref({});

/* ================= VOICE TO TEXT (Web Speech API) ================= */
const isListening = ref(false);
let recognition = null;

onMounted(() => {
  // Load logged-in user info (supports JSON or JWT in keys: user, me)
  loggedInUser.value = loadUserFromStorage();

  // Load email history (localStorage first, then sessionStorage fallback)
  const storedHistory =
    localStorage.getItem('emailHistory') || sessionStorage.getItem('emailHistory');
  history.value = storedHistory ? JSON.parse(storedHistory) : [];

  const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
  if (!SR) {
    $q.notify({
      type: 'warning',
      message: 'Voice input not supported in this browser.',
    });
    return;
  }

  recognition = new SR();
  recognition.continuous = true;
  recognition.interimResults = true;
  recognition.lang = speechLanguage.value || 'en-US';

  recognition.onresult = (event) => {
    let finalText = '';
    let interimText = '';
    for (let i = event.resultIndex; i < event.results.length; i++) {
      const t = event.results[i][0].transcript;
      if (event.results[i].isFinal) finalText += t;
      else interimText += t;
    }

    const heard = (finalText || interimText).toLowerCase().trim();
    if (heard.includes('stop listening')) {
      stopVoice();
      return;
    }

    if (finalText) appendAsBullets(finalText);
  };

  recognition.onerror = (e) => {
    $q.notify({ type: 'negative', message: `Voice error: ${e.error}` });
    stopVoice();
  };

  recognition.onend = () => {
    isListening.value = false;
  };
});

onBeforeUnmount(() => {
  stopVoice();
});

function appendAsBullets(text) {
  const lines = text
    .replace(/\s+/g, ' ')
    .split(/[.!?;\n]\s*/g)
    .map((s) => s.trim())
    .filter(Boolean);

  lines.forEach((line) => {
    const prefix = bullets.value && !bullets.value.endsWith('\n') ? '\n' : '';
    bullets.value += `${prefix}- ${line}`;
  });
}

function startVoice() {
  if (!recognition) return;
  if (!speechLanguage.value) {
    notify('negative', 'Please select a speech language first.');
    return;
  }

  try {
    recognition.lang = speechLanguage.value;
    recognition.start();
    isListening.value = true;
  } catch (e) {
    // starting twice can throw; ignore
  }
}

function stopVoice() {
  if (!recognition) return;
  try {
    recognition.stop();
  } catch (e) {
    console.warn('Speech recognition already stopped:', e.message);
  }
  isListening.value = false;
}

function toggleVoice() {
  isListening.value ? stopVoice() : startVoice();
}

/* ================= end VOICE TO TEXT ================= */

/* Email display helpers */
function toggleExpand(index) {
  expandedEmails.value[index] = !expandedEmails.value[index];
}

function copyEmail(email) {
  navigator.clipboard
    .writeText(email.body)
    .then(() => {
      notify('positive', 'Email body copied to clipboard');
    })
    .catch(() => {
      notify('negative', 'Failed to copy to clipboard');
    });
}

function resendEmail(email) {
  bullets.value = email.body;
  draft.value = email;
  editMode.value = true;
  showHistory.value = false;
  // Populate recipients if available
  if (email.to_email) {
    toEmails.value = email.to_email.split(',').map(e => e.trim());
  }
  notify('info', 'Email loaded for resending');
}

/* ------------------ actions ------------------ */
async function onGenerate() {
  if (!bullets.value.trim()) {
    notify('negative', 'Please enter some bullet points first.');
    return;
  }
  loadingGenerate.value = true;
  try {
    const { data } = await api.post('email/generate/', {
      bullets: bullets.value,
      tone: tone.value,
      language: language.value,
    });
    // Replace [Your Name] placeholder with logged-in user name (supports multiple fields)
    if (userDisplayName.value && data.body) {
      // Prefer to break line after Best regards,
      data.body = data.body.replace(
        /Best\s+regards,?\s*\[Your Name\]/i,
        `Best regards,\n${userDisplayName.value}`,
      );
      // Generic replacement in case pattern differs
      data.body = data.body.replace(/\[Your Name\]/gi, userDisplayName.value);
    }
    draft.value = data;
    editMode.value = true;
    showHistory.value = false; // Hide history when showing draft
    notify('positive', 'Email generated.');
  } catch (err) {
    console.error(err);
    notify('negative', 'Failed to generate email.');
  } finally {
    loadingGenerate.value = false;
  }
}

async function onSend() {
  if (!draft.value) return;
  if (!toEmails.value || toEmails.value.length === 0) {
    notify('negative', 'Enter at least one recipient email.');
    return;
  }

  // Validate emails before sending
  const validationResult = validateEmails(toEmails.value);
  if (validationResult !== true) {
    notify('negative', validationResult);
    return;
  }

  loadingSend.value = true;
  try {
    const { data } = await api.post('email/send/', {
      id: draft.value.id,
      to_email: toEmails.value.join(', '), // Join multiple emails with comma-space
      provider: provider.value,
      subject: draft.value.subject,
      body: draft.value.body,
    });
    draft.value = data;
    if (data.status === 'SENT') {
      notify('positive', `Email sent to ${toEmails.value.length} recipient(s).`);
      // Save to history (keep last 3)
      history.value.unshift(data);
      history.value = history.value.slice(0, 3);
      const serializedHistory = JSON.stringify(history.value);
      localStorage.setItem('emailHistory', serializedHistory);
      sessionStorage.setItem('emailHistory', serializedHistory);
      // Clear bullets and collapse draft
      bullets.value = '';
      toEmails.value = []; // Clear recipients
      draft.value = null;
      showHistory.value = true; // Show history after sending
    } else {
      notify('warning', data.error || 'Attempted to send (FAILED).');
    }
  } catch (err) {
    console.error(err);
    notify('negative', 'Failed to send email.');
  } finally {
    loadingSend.value = false;
  }
}
</script>

<style scoped>
/* Optional: extra RTL tweaks if needed */
.rtl-bullets :deep(textarea) {
  direction: rtl;
}

/* Email body styles */
.email-body-text {
  line-height: 1.6;
  color: #333;
  font-size: 14px;
  word-wrap: break-word;
}

.multiline-placeholder :deep(textarea::placeholder) {
  white-space: pre-line;
}

/* Quasar textarea placeholder (additional selector to catch native element) */
.multiline-placeholder :deep(.q-field__native textarea::placeholder) {
  white-space: pre-line;
}

.email-body-text.collapsed {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.email-body-text.expanded {
  white-space: pre-wrap;
  max-height: 220px;
  overflow-y: auto;
}

.cursor-pointer {
  cursor: pointer;
}
</style>
